import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Calendar, Users, Layers, KanbanSquare, GanttChartSquare } from 'lucide-react'
import { useProjectStore } from '../../store/projectStore'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import MobileKanbanBoard from '../../components/projects/MobileKanbanBoard'
import GanttTimelineList from '../../components/projects/GanttTimelineList'

const STATUS_STYLES: Record<string, string> = {
  planning: 'bg-gray-100 text-gray-700',
  active: 'bg-green-100 text-green-700',
  on_hold: 'bg-yellow-100 text-yellow-700',
  completed: 'bg-blue-100 text-blue-700',
  cancelled: 'bg-red-100 text-red-700',
}

type Tab = 'board' | 'timeline'

export default function ProjectMobilePage() {
  const { id } = useParams<{ id: string }>()
  const projectId = Number(id)
  const { currentProject, fetchProject, isLoading } = useProjectStore()
  const [tab, setTab] = useState<Tab>('board')

  useEffect(() => {
    if (projectId) fetchProject(projectId)
  }, [projectId, fetchProject])

  if (isLoading && !currentProject) return <LoadingSpinner />
  if (!currentProject || currentProject.id !== projectId) {
    return (
      <div className="p-6 text-center text-sm text-gray-500">
        Project not found. <Link to="/projects" className="text-primary-600">Back</Link>
      </div>
    )
  }

  return (
    <div className="pb-4">
      <div className="bg-white border-b border-gray-200 p-4">
        <Link to="/projects" className="inline-flex items-center gap-1 text-xs text-gray-600 mb-2">
          <ArrowLeft className="h-3.5 w-3.5" />
          Projects
        </Link>

        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span className="h-3 w-3 rounded-full" style={{ backgroundColor: currentProject.color || '#3b82f6' }} />
            <h1 className="text-base font-semibold text-gray-900 truncate">{currentProject.name}</h1>
          </div>
          <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${STATUS_STYLES[currentProject.status]}`}>
            {currentProject.status.replace('_', ' ')}
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1">
            <Layers className="h-3 w-3" />
            {currentProject.task_count ?? 0} tasks
          </span>
          <span className="inline-flex items-center gap-1">
            <Users className="h-3 w-3" />
            {currentProject.member_count ?? 0}
          </span>
          {currentProject.end_date && (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              Due {new Date(currentProject.end_date).toLocaleDateString()}
            </span>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white">
        <div className="flex">
          <button
            onClick={() => setTab('board')}
            className={`flex-1 inline-flex items-center justify-center gap-2 border-b-2 py-3 text-sm font-medium transition-colors ${
              tab === 'board' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500'
            }`}
          >
            <KanbanSquare className="h-4 w-4" />
            Board
          </button>
          <button
            onClick={() => setTab('timeline')}
            className={`flex-1 inline-flex items-center justify-center gap-2 border-b-2 py-3 text-sm font-medium transition-colors ${
              tab === 'timeline' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500'
            }`}
          >
            <GanttChartSquare className="h-4 w-4" />
            Timeline
          </button>
        </div>
      </div>

      <div className="mt-3">
        {tab === 'board' && <MobileKanbanBoard projectId={projectId} />}
        {tab === 'timeline' && <GanttTimelineList projectId={projectId} />}
      </div>
    </div>
  )
}
